import { COMPANY_STATUS_LABELS } from './companyStatus.ts';
import type { ActivityEvent, CompanyStatus } from './types.ts';

// ----------------------------------------------------------------------
// Activity log formatter.
//
// Turns a raw activity_log row (event_type + payload + resolved labels) into
// a human-readable {title, detail?, actor}. Two families of events exist:
//
//   1. Trigger events written by the Postgres log_activity() trigger. Their
//      event_type is `<table>.<op>` where op is the LOWERCASE tg_op
//      ('insert' | 'update' | 'delete') and payload is
//      { op: 'INSERT'|'UPDATE'|'DELETE', table, row }. payload.row is the NEW
//      row for insert/update and the OLD row for delete — there is no
//      before/after pair, so we describe the resulting state only.
//
//   2. Cron events written directly by apps/api/src/scheduled.ts (e.g.
//      'company.invite_reminder.7d', 'submission_file.expired'). These carry a
//      small bespoke payload.
//
// Every branch must tolerate missing payload keys: activity rows are an
// append-only audit trail and old rows may predate a column. Never throw.
// ----------------------------------------------------------------------

export type ActivityDescription = {
  /** One-line human summary, e.g. "Submission approved". */
  title: string;
  /** Optional secondary line with extra context, e.g. a document name. */
  detail?: string;
  /** Who performed the action, e.g. "Acme Pty Ltd" or "System". */
  actor: string;
};

/**
 * Row shape the formatter consumes — the stored event plus the resolved
 * actor_label / company_name the API attaches (already part of ActivityEvent).
 */
export type ActivityRow = ActivityEvent;

type TriggerPayload = {
  op?: string;
  table?: string;
  row?: Record<string, unknown> | null;
};

// Internal helper return shape. `detail?: string | undefined` lets a helper
// either omit `detail` or hand back an explicit `undefined` — the project
// enables exactOptionalPropertyTypes, under which the public
// ActivityDescription's `detail?: string` would reject an explicit `undefined`.
// formatActivity normalises this back to the public shape.
type TitleDetail = { title: string; detail?: string | undefined };

// ---- small helpers ----------------------------------------------------

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function asString(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

/** Prettify a dot/underscore-separated machine code into Title-ish text. */
export function prettifyCode(code: string): string {
  const words = code
    .replace(/[._]/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (words.length === 0) return code;
  return words
    .map((w, i) => (i === 0 ? w.charAt(0).toUpperCase() + w.slice(1) : w))
    .join(' ');
}

const SUBMISSION_STATUS_LABELS: Record<string, string> = {
  pending: 'Pending review',
  approved: 'Approved',
  rejected: 'Rejected',
};

/**
 * Who performed the action. Admin actors show their resolved name/email;
 * company actors show the company name; system/cron actors show "System".
 */
export function describeActor(row: ActivityRow): string {
  if (row.actor_type === 'system') return 'System';
  if (row.actor_type === 'admin') {
    return asString(row.actor_label) ?? 'An administrator';
  }
  // company actor
  return asString(row.company_name) ?? asString(row.actor_label) ?? 'The vendor';
}

// ---- trigger-event humanisers ----------------------------------------

function describeSubmission(op: string, row: Record<string, unknown>): TitleDetail {
  if (op === 'DELETE') return { title: 'Submission removed' };
  if (op === 'INSERT') return { title: 'Submission created' };
  // UPDATE — the most informative case; lead with the resulting status.
  const status = asString(row.status);
  if (status && SUBMISSION_STATUS_LABELS[status]) {
    const label = SUBMISSION_STATUS_LABELS[status];
    const comment = asString(row.reviewer_comment);
    if (status === 'rejected') {
      return {
        title: 'Submission rejected',
        detail: comment ? `Reviewer comment: ${comment}` : undefined,
      };
    }
    if (status === 'approved') {
      return { title: 'Submission approved' };
    }
    return { title: `Submission status changed to ${label}` };
  }
  return { title: 'Submission updated' };
}

function describeSubmissionFile(op: string, row: Record<string, unknown>): TitleDetail {
  const name = asString(row.file_name);
  const detail = name ? `File: ${name}` : undefined;
  if (op === 'INSERT') return { title: 'Document uploaded', detail };
  if (op === 'DELETE') return { title: 'Document removed', detail };
  // UPDATE — vendor "remove" is a soft-delete that stamps superseded_at (kept
  // for audit history), so it surfaces here as an UPDATE rather than a DELETE.
  // Check that first: a superseded row is a removal regardless of any expiry
  // it also carries.
  if (asString(row.superseded_at)) {
    return { title: 'Document removed', detail };
  }
  // Otherwise an UPDATE is most often an expiry date being set/changed.
  const expiresAt = asString(row.expires_at);
  if (expiresAt) {
    return { title: 'Document expiry updated', detail: name ? `${name} — expires ${expiresAt}` : `Expires ${expiresAt}` };
  }
  return { title: 'Document updated', detail };
}

function describeSubmissionAdoption(op: string, row: Record<string, unknown>): TitleDetail {
  const mode = asString(row.mode);
  if (op === 'DELETE') return { title: 'Policy document choice removed' };
  if (mode === 'adopted') {
    return { title: 'Standard policy adopted', detail: 'Vendor adopted the supplied policy document' };
  }
  if (mode === 'uploaded') {
    return { title: 'Own policy uploaded', detail: 'Vendor uploaded their own policy document' };
  }
  return { title: op === 'INSERT' ? 'Policy document choice recorded' : 'Policy document choice updated' };
}

function describeCompany(op: string, row: Record<string, unknown>): TitleDetail {
  if (op === 'INSERT') {
    const name = asString(row.company_name);
    return { title: 'Vendor invited', detail: name ?? undefined };
  }
  if (op === 'DELETE') {
    const name = asString(row.company_name);
    return { title: 'Vendor deleted', detail: name ?? undefined };
  }
  // UPDATE — surface a status change using the shared status labels so the
  // wording (incl. "Not Compliant" for the legacy 'expired' status) never
  // drifts from the rest of the admin UI.
  const status = asString(row.status);
  if (status && status in COMPANY_STATUS_LABELS) {
    const label = COMPANY_STATUS_LABELS[status as CompanyStatus];
    return { title: `Vendor status set to ${label}` };
  }
  return { title: 'Vendor record updated' };
}

const TABLE_NOUNS: Record<string, string> = {
  documents: 'Document template',
  questions: 'Question',
  question_categories: 'Question category',
  questionnaires: 'Questionnaire',
  drivers: 'Driver',
  settings: 'Settings',
  review_notifications: 'Review note',
};

function describeGenericTable(table: string, op: string): TitleDetail {
  const noun = TABLE_NOUNS[table] ?? prettifyCode(table);
  const verb = op === 'INSERT' ? 'created' : op === 'DELETE' ? 'deleted' : 'updated';
  // 'Settings updated' reads better than 'Settings created/deleted'; settings
  // is a singleton row that is only ever updated in practice.
  if (table === 'settings') return { title: 'Settings updated' };
  return { title: `${noun} ${verb}` };
}

function describeTriggerEvent(eventType: string, payload: TriggerPayload): TitleDetail {
  const table = asString(payload.table) ?? eventType.split('.')[0] ?? eventType;
  const op = (asString(payload.op) ?? eventType.split('.')[1] ?? '').toUpperCase();
  const row = asRecord(payload.row) ?? {};

  switch (table) {
    case 'submissions':
      return describeSubmission(op, row);
    case 'submission_files':
      return describeSubmissionFile(op, row);
    case 'submission_adoptions':
      return describeSubmissionAdoption(op, row);
    case 'companies':
      return describeCompany(op, row);
    default:
      return describeGenericTable(table, op);
  }
}

// ---- cron-event humanisers --------------------------------------------

function describeCronEvent(eventType: string, payload: Record<string, unknown>): TitleDetail | null {
  // company.invite_reminder.<n>d
  const inviteMatch = /^company\.invite_reminder\.(\d+)d$/.exec(eventType);
  if (inviteMatch) {
    const days = inviteMatch[1];
    return { title: `Invite reminder sent (${days} days)` };
  }

  // company.compliance_expiring.<n>d
  const complianceMatch = /^company\.compliance_expiring\.(\d+)d$/.exec(eventType);
  if (complianceMatch) {
    const days = complianceMatch[1];
    return { title: `Compliance-expiry reminder sent (${days} days)` };
  }

  if (eventType === 'submission_file.expiry_reminder') {
    const name = asString(payload.file_name);
    const days = typeof payload.days_remaining === 'number' ? payload.days_remaining : null;
    return {
      title: 'Document expiry reminder sent',
      detail:
        name && days !== null
          ? `${name} — expires in ${days} day${days === 1 ? '' : 's'}`
          : (name ?? undefined),
    };
  }

  if (eventType === 'submission_file.expired') {
    const name = asString(payload.file_name);
    return {
      title: 'Document marked expired — vendor notified',
      detail: name ?? undefined,
    };
  }

  return null;
}

// ---- relative time ----------------------------------------------------

/**
 * Compact relative time, e.g. "just now", "5m ago", "3h ago", "2d ago".
 * Beyond a week it returns the local date so the audit trail stays precise.
 * `now` is injectable for deterministic tests.
 */
export function relativeTime(iso: string, now: number = Date.now()): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return iso;
  const diffMs = now - then;
  if (diffMs < 0) return 'just now';
  const sec = Math.floor(diffMs / 1000);
  if (sec < 45) return 'just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  return new Date(then).toLocaleDateString();
}

// ---- public entry point -----------------------------------------------

/**
 * Format a single activity row into a human-readable description. Pure and
 * total: any unrecognised event or missing payload yields a graceful
 * prettified fallback rather than throwing.
 */
export function formatActivity(row: ActivityRow): ActivityDescription {
  const actor = describeActor(row);
  const eventType = typeof row.event_type === 'string' ? row.event_type : '';
  const payload = asRecord(row.payload) ?? {};

  // A trigger event carries an explicit op + table; that is the reliable
  // discriminator (cron events never set those keys).
  if (asString(payload.op) && asString(payload.table)) {
    const { title, detail } = describeTriggerEvent(eventType, payload as TriggerPayload);
    return detail ? { title, detail, actor } : { title, actor };
  }

  const cron = describeCronEvent(eventType, payload);
  if (cron) {
    return cron.detail ? { title: cron.title, detail: cron.detail, actor } : { title: cron.title, actor };
  }

  // Last resort: prettify the raw event code so the UI never shows a blank or
  // a crash for an event type added after this formatter was written.
  return { title: eventType ? prettifyCode(eventType) : 'Activity', actor };
}
